// Vendored from the JETCAM DXF Viewer (src/render/scene.ts), 2026-09-03.
// Origin: C:/inetpub/wwwroot/Jetcost/dxfviewer — that project is untouched.
// Unmodified. The ../geometry and ../parse imports resolve to the shims
// beside this directory, which re-export the engine.

import { type DisplayList } from "../geometry/index.ts";
import { type LayerRecord, type LtypeRecord } from "../parse/index.ts";
import { type Rgb, resolveColor, resolveLinetype, rgbToCss } from "./colors.ts";
import { resolveDashPattern } from "./linetypes.ts";
import { type IndexedPrimitive, SpatialIndex } from "./spatial.ts";

/**
 * Everything about a drawing that the renderer needs but that does not change
 * from frame to frame, worked out once.
 *
 * This exists because of a measurement. Resolving a primitive's colour and
 * formatting it as a CSS string is cheap on its own and ruinous fifty thousand
 * times a frame: on the stress nest it accounted for roughly two thirds of the
 * frame budget, in Map lookups and throwaway strings, before a single pixel
 * was drawn. Colours only change when the theme or the layer table changes, so
 * they are resolved here and reduced to a small integer per primitive. The
 * per-frame loop then buckets by integer into reused arrays and never
 * allocates a string at all.
 *
 * The same argument applies to visibility: whether a layer is off or frozen in
 * the file is fixed, so it is folded in here rather than looked up per frame.
 */
export class Scene {
  readonly display: DisplayList;
  readonly index: SpatialIndex;

  /** Distinct CSS colours used by this drawing. */
  palette: string[] = [];
  /** Per primitive: index into `palette`. */
  colorSlot: Int32Array;

  /**
   * Distinct (colour, dash pattern) combinations used by this drawing —
   * `palette`'s counterpart for stroking, which needs both at once since
   * `ctx.setLineDash` is canvas state exactly like `ctx.strokeStyle`. Kept
   * separate from `palette`/`colorSlot` because `draw.ts`'s text rendering
   * only ever wants plain colour, never a dash pattern.
   */
  stylePalette: { readonly color: string; readonly dash: readonly number[] }[] = [];
  /** Per primitive: index into `stylePalette`. */
  styleSlot: Int32Array;

  /** Per primitive: visible according to the file itself. */
  private fileVisible: Uint8Array;
  /** Per primitive: `fileVisible` and not switched off by the user. */
  private visible: Uint8Array;

  private layers: ReadonlyMap<string, LayerRecord>;
  private ltypes: ReadonlyMap<string, LtypeRecord>;
  /** When set, every primitive resolves to this colour instead of its own layer/entity colour — compare mode's Overlay "file B" tint. */
  private overrideColor: Rgb | undefined;
  private hiddenLayers: ReadonlySet<string> = new Set();
  private hiddenTypes: ReadonlySet<string> = new Set();
  private lockedLayers: ReadonlySet<string> = new Set();

  /**
   * Per primitive: may the pointer pick or snap to this?
   *
   * Two things make geometry unpickable. Hidden geometry, obviously — a
   * tooltip for something not on screen is a bug, and it was one until this
   * flag existed. And locked geometry, which is the point of the padlock in
   * the Layers tab: kept visible as context, but ignored by hover, selection
   * and snapping, so the cursor stops catching on the hatch and the dimension
   * text while you are trying to measure the part.
   */
  private pickable: Uint8Array;

  /**
   * Primitive ids grouped by layer, so toggling a layer touches only that
   * layer's primitives instead of walking the whole drawing. Doubles as the
   * per-layer counts the Layers tab shows.
   */
  readonly layerBuckets = new Map<string, number[]>();

  /**
   * Primitive ids grouped by source entity type (`EntityRef.sourceType`, e.g.
   * `LWPOLYLINE`, `MTEXT`), the same way `layerBuckets` groups by layer. Backs
   * the Entities tab's per-type show/hide and its counts.
   */
  readonly typeBuckets = new Map<string, number[]>();

  /**
   * Per-colour buckets, reused across frames. Owned here rather than allocated
   * inside the draw loop so that a full-nest frame does not hand the collector
   * a fresh array of fifty thousand entries sixty times a second.
   */
  private readonly buckets: IndexedPrimitive[][] = [];

  /** Empty buckets sized to the palette, ready for this frame. */
  takeBuckets(): IndexedPrimitive[][] {
    while (this.buckets.length < this.palette.length) this.buckets.push([]);
    for (const bucket of this.buckets) bucket.length = 0;
    return this.buckets;
  }

  /** `stylePalette`'s counterpart to `buckets`/`takeBuckets`, for the stroke loop. */
  private readonly styleBuckets: IndexedPrimitive[][] = [];

  /** Empty buckets sized to the style palette, ready for this frame. */
  takeStyleBuckets(): IndexedPrimitive[][] {
    while (this.styleBuckets.length < this.stylePalette.length) this.styleBuckets.push([]);
    for (const bucket of this.styleBuckets) bucket.length = 0;
    return this.styleBuckets;
  }

  constructor(
    display: DisplayList,
    layers: ReadonlyMap<string, LayerRecord>,
    background: Rgb,
    showInvisible = false,
    ltypes: ReadonlyMap<string, LtypeRecord> = new Map(),
    overrideColor?: Rgb,
  ) {
    this.display = display;
    this.index = new SpatialIndex(display.primitives);
    this.layers = layers;
    this.ltypes = ltypes;

    const count = display.primitives.length;
    this.colorSlot = new Int32Array(count);
    this.styleSlot = new Int32Array(count);
    this.fileVisible = new Uint8Array(count);
    this.visible = new Uint8Array(count);
    this.pickable = new Uint8Array(count);

    this.restyle(background, layers, showInvisible, ltypes, overrideColor);
  }

  /** Recompute colours and file-level visibility. Cheap enough to redo on theme change. */
  restyle(
    background: Rgb,
    layers: ReadonlyMap<string, LayerRecord> = this.layers,
    showInvisible = false,
    ltypes: ReadonlyMap<string, LtypeRecord> = this.ltypes,
    overrideColor: Rgb | undefined = this.overrideColor,
  ): void {
    this.layers = layers;
    this.ltypes = ltypes;
    this.overrideColor = overrideColor;
    const context = { background, layers };
    const slots = new Map<string, number>();
    const styleSlots = new Map<string, number>();
    this.palette = [];
    this.stylePalette = [];
    this.layerBuckets.clear();
    this.typeBuckets.clear();

    const primitives = this.display.primitives;
    for (let i = 0; i < primitives.length; i += 1) {
      const p = primitives[i]!;

      const bucket = this.layerBuckets.get(p.layer);
      if (bucket) bucket.push(i);
      else this.layerBuckets.set(p.layer, [i]);

      const type = p.ref.sourceType;
      const typeBucket = this.typeBuckets.get(type);
      if (typeBucket) typeBucket.push(i);
      else this.typeBuckets.set(type, [i]);

      const css = overrideColor
        ? rgbToCss(overrideColor)
        : rgbToCss(resolveColor(p.color, p.layer, context, p.trueColor));
      let slot = slots.get(css);
      if (slot === undefined) {
        slot = this.palette.length;
        this.palette.push(css);
        slots.set(css, slot);
      }
      this.colorSlot[i] = slot;

      const linetypeName = resolveLinetype(p.linetype, p.layer, layers);
      const styleKey = `${css}|${linetypeName}`;
      let styleSlot = styleSlots.get(styleKey);
      if (styleSlot === undefined) {
        styleSlot = this.stylePalette.length;
        this.stylePalette.push({ color: css, dash: resolveDashPattern(linetypeName, ltypes) });
        styleSlots.set(styleKey, styleSlot);
      }
      this.styleSlot[i] = styleSlot;

      const layer = layers.get(p.layer);
      const offInFile = layer !== undefined && (layer.off || layer.frozen);
      this.fileVisible[i] = (p.hidden && !showInvisible) || offInFile ? 0 : 1;
    }

    this.applyHiddenLayers();
  }

  /** Layers kept on screen but ignored by the pointer. */
  setLockedLayers(locked: ReadonlySet<string> | undefined): void {
    this.lockedLayers = locked ?? new Set<string>();
    this.applyPickable();
  }

  isLayerLocked(layer: string): boolean {
    return this.lockedLayers.has(layer);
  }

  /** Whether the pointer may hover, select or snap to this primitive. */
  isPickable(id: number): boolean {
    return this.pickable[id] === 1;
  }

  private applyPickable(): void {
    const primitives = this.display.primitives;
    for (let i = 0; i < primitives.length; i += 1) {
      this.pickable[i] =
        this.visible[i] === 1 && !this.lockedLayers.has(primitives[i]!.layer) ? 1 : 0;
    }
  }

  setHiddenLayers(hidden: ReadonlySet<string> | undefined): void {
    const next = hidden ?? new Set<string>();

    // Only layers whose state actually changed are touched. Toggling one layer
    // of a 50,000-primitive nest then costs that layer's primitives, not all
    // of them, which is what makes the checkbox feel instant.
    for (const [layer, ids] of this.layerBuckets) {
      const was = this.hiddenLayers.has(layer);
      const now = next.has(layer);
      if (was === now) continue;
      for (const id of ids) this.visible[id] = this.computeVisible(id, next, this.hiddenTypes) ? 1 : 0;
    }

    this.hiddenLayers = next;
    this.applyPickable();
  }

  /** Entity-type counterpart to `setHiddenLayers`, same incremental shape. */
  setHiddenTypes(hidden: ReadonlySet<string> | undefined): void {
    const next = hidden ?? new Set<string>();

    for (const [type, ids] of this.typeBuckets) {
      const was = this.hiddenTypes.has(type);
      const now = next.has(type);
      if (was === now) continue;
      for (const id of ids) this.visible[id] = this.computeVisible(id, this.hiddenLayers, next) ? 1 : 0;
    }

    this.hiddenTypes = next;
    this.applyPickable();
  }

  /**
   * Whether primitive `id` should be visible against explicit hidden-layers
   * and hidden-types sets. A primitive can be hidden by either axis, so both
   * are always checked — the incremental callers above pass the axis they are
   * about to commit for the one that changed, and `this`'s current value for
   * the one that didn't.
   */
  private computeVisible(
    id: number,
    hiddenLayers: ReadonlySet<string>,
    hiddenTypes: ReadonlySet<string>,
  ): boolean {
    if (this.fileVisible[id] !== 1) return false;
    const p = this.display.primitives[id]!;
    return !hiddenLayers.has(p.layer) && !hiddenTypes.has(p.ref.sourceType);
  }

  /** Full recompute, used after a restyle when every flag may have moved. */
  private applyHiddenLayers(): void {
    const primitives = this.display.primitives;
    for (let i = 0; i < primitives.length; i += 1) {
      this.visible[i] = this.computeVisible(i, this.hiddenLayers, this.hiddenTypes) ? 1 : 0;
    }
    this.applyPickable();
  }

  /** Layer names present in the geometry, with how many primitives each holds. */
  layerCounts(): Map<string, number> {
    const out = new Map<string, number>();
    for (const [layer, ids] of this.layerBuckets) out.set(layer, ids.length);
    return out;
  }

  isLayerHidden(layer: string): boolean {
    return this.hiddenLayers.has(layer);
  }

  /** Source entity types present in the geometry, with how many primitives each holds. */
  typeCounts(): Map<string, number> {
    const out = new Map<string, number>();
    for (const [type, ids] of this.typeBuckets) out.set(type, ids.length);
    return out;
  }

  isTypeHidden(type: string): boolean {
    return this.hiddenTypes.has(type);
  }

  /** The LAYER table this scene was styled against, for resolving BYLAYER outside the draw loop (e.g. the hover tooltip). */
  get layerTable(): ReadonlyMap<string, LayerRecord> {
    return this.layers;
  }

  isVisible(id: number): boolean {
    return this.visible[id] === 1;
  }
}
